"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import { BookingSchedule } from "@/lib/types/schedule-types";
import { RecommendedVendor } from "~/components/listings/RecommendedVendors/RecommendedVendors.types";
import {
  IAttachment,
  IFavorites,
  IListing,
  IListingShare,
  IPrice,
  IRecommendedListings,
  IReview,
  IStructuredAmenity,
  IStructuredReview,
} from "~/types/api-types";
import { ListingImageCondensed } from "~/types/listing.types";
import { ILocalCancellationPolicy } from "~/types/local-types";
// Types for critical data (initial data)
export interface CriticalListingData {
  amenities: Record<string, IStructuredAmenity[]>;
  attendeesMin: IListing["attendees_min"];
  cancellationPolicy?: ILocalCancellationPolicy;
  canonicalLocData: IListing["canonical_loc_data"];
  category: string; // This isn't on the ListingDetail response data - so it's unknown to what it is - originally IListing["category"] - but it's unknown since it's not on the ListingDetail type
  cityBreadcrumb: string;
  cityGoogle: string;
  cleaningFee: IListing["cleaning_fee"];
  cleaningInfo: IListing["cleaning_info"];
  coverImg: string;
  currencySymbol: IListing["currency_symbol"];
  dateCreated: IListing["date_created"];
  dateModified: IListing["date_modified"];
  dayRateDiscountSet: boolean;
  defaultBookingSchedule: BookingSchedule;
  description: IListing["description"];
  emailShare: string;
  eventDetails: IListing["event_details"];
  eventFaqDetails?: IListing["event_faq_details"];
  fandbAppliesTo: IListing["fandb_applies_to"];
  fandbRequired: IListing["fandb_required"];
  fbShare: IListingShare;
  fullService: IListing["full_service"];
  hasAddOns: IListing["has_add_ons"];
  hasPriceByDay: IListing["has_price_by_day"];
  hasSurveillance: boolean;
  highlights?: {
    title: string;
    subtitle: string;
    icon: string;
  }[];
  images: ListingImageCondensed[];
  inventory: IListing["inventory"];
  isInstantBookActive: IListing["is_instant_book_active"];
  isPowerHost?: boolean;
  latitude: IListing["latitude"];
  listingAttendeePricing: IListing["attendee_pricing"];
  listingAwards: IListing["awards"];
  listingCapacity: number;
  listingCity: IListing["city"];
  listingCountry: IListing["country"];
  listingCurrency: IPrice["currency"];
  listingDayRateDiscount: IListing["day_rate_discount"];
  listingFavoriteCount: IListing["listing_favorite_count"];
  listingHostResponsiveness: IListing["host_responsiveness"];
  listingId: IListing["_id"];
  listingMinimumDuration: IListing["minimum_duration"];
  listingOwnerFirstName: string;
  listingOwnerId: IListing["ownerId"];
  listingParentSpaceId: IListing["parentSpaceId"];
  listingPrice: IPrice["value"];
  listingPriceRange: IListing["price_range"];
  listingRules: IListing["rules"];
  listingState: IListing["state"];
  listingStatus: IListing["status"];
  listingVenueName: IListing["venue_name"];
  longitude: IListing["longitude"];
  mapUrl: string;
  metro: IListing["metro"];
  minAge: IListing["min_age"];
  neighborhood: IListing["neighborhood"];
  neighborhoodBreadcrumb?: string;
  numOfAmenities: number;
  opHours: IListing["ophours"];
  ownerAvatar?: string;
  ownerAvatarId: IListing["owner_avatar_id"];
  ownerLastName: string;
  ownerName: string;
  pageTitle: IListing["page_title"];
  parkingDetails: IListing["parking_info"];
  payAtVenue?: IListing["pay_at_venue"];
  priceRange: IListing["price_range"];
  prices: IListing["prices"];
  productionDetails: IListing["production_details"];
  spaceReviewCount: IListing["space_review_count"];
  spaceReviewStars: IListing["space_review_stars"];
  spaceType: IListing["space_type_tag"];
  sqft: IListing["sqft"];
  stateGoogle: string;
  surveillance: IListing["surveillance_info"];
  surveillanceDescription?: string;
  title: IListing["title"];
  twitterShare: IListingShare;
  // Urgency messaging
  urgency?: boolean;
  useType: IListing["use_type"];
  useTypeBreadcrumb: string;
  use_type?: IListing["use_type"];
  vendors?: RecommendedVendor[];
  videos?: IListing["embed_media"];
  zipGoogle: IListing["zip_google"];
}

// Types for secondary data (loaded after initial render)
export interface SecondaryListingData {
  reviews?: IReview[];
  listingStructuredReviews?: IStructuredReview[];
  recommendedListings?: IRecommendedListings[];
  allAttachments?: IAttachment[];
  favorites?: IFavorites;
}

// Combined listing data type
export type ListingData = CriticalListingData & Partial<SecondaryListingData>;

// Context type
interface ListingDataContextType {
  data: ListingData;
  secondaryDataFetched: boolean;
  updateData: (
    newData: Partial<ListingData>,
    isSecondaryData?: boolean,
  ) => void;
}

// Create the context with default values
const ListingDataContext = createContext<ListingDataContextType | undefined>(
  undefined,
);

// Provider component
export function ListingPageDataProvider({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData: CriticalListingData;
}) {
  const [data, setData] = useState<ListingData>(initialData);
  const [secondaryDataFetched, setSecondaryDataFetched] = useState(false);

  // Update function that allows partial updates to the data
  const updateData = useCallback(
    (newData: Partial<ListingData>, isSecondaryData = false) => {
      setData((currentData) => ({
        ...currentData,
        ...newData,
      }));
      // indicates that secondary data has been fetched and is ready to be used
      if (isSecondaryData) {
        setSecondaryDataFetched(true);
      }
    },
    [],
  );

  return (
    <ListingDataContext.Provider
      value={{ data, secondaryDataFetched, updateData }}
    >
      {children}
    </ListingDataContext.Provider>
  );
}

// Hook to consume listing page data from context
export const useListingData = (): ListingDataContextType => {
  const context = useContext(ListingDataContext);

  if (context === undefined) {
    throw new Error(
      "useListingData must be used within a ListingPageDataProvider",
    );
  }

  return context;
};
